/**
 * AP-34 6. — **Erfassungsmaske**: links das Original, rechts die Erfassung in vier Abschnitten
 * (Kopf, Positionen, Summen, Prüfung); am Telefon untereinander.
 *
 * Gespeichert wird feldweise ohne Knopf (Nachtrag 1, 7.). Jede Antwort trägt die ganze Rechnung —
 * Summen, Steuer und Prüfung stehen nach jeder Änderung neu da.
 */
import { useCallback, useEffect, useState, type CSSProperties, type ReactNode } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { AlertTriangle, ArrowLeft, CheckCircle2, Info, Send, Trash2, XCircle } from 'lucide-react'
import { INCOMING_INVOICE_KINDS, erfuelltRecht, geldText, kostenzielZumKopf, type IncomingCostTarget } from '@symphenia/shared-types'
import { GeldFeldFuer, geldEingabe } from '@/components/geldfeld'
import { Vorschlaege, useSuche } from '@/components/zeilenblock'
import { apiErrorText } from '@/lib/api-error'
import { useAuthStore } from '@/lib/auth.store'
import { bestaetige, frage } from '@/lib/dialoge'
import { pageWide } from '@/lib/form-styles'
import { Abschnitt, FeldZeile, Feldgruppe, SchalterZeile, datumOhneVorlage, feldKlasse, useFeld, useSpeicherstelle, type Datensatz, type Speicherstelle } from '@/lib/speichern'
import { useTerminology } from '@/lib/useTerminology'
import { freigabenZahlNeu } from '@/components/layout/FreigabenZahl'
import { OriginalAnzeige, type OriginalZiel } from '@/components/original/OriginalAnzeige'
import type { Fundstelle } from '@/components/original/fundstellen'
import { useTeilung } from '@/lib/teilung'
import { cancelIncomingInvoice, completeIncomingInvoice, deleteIncomingInvoice, fetchIncomingFrame, fetchIncomingInvoice, fetchIncomingOriginal, lookupProjects, lookupSuppliers, patchIncomingInvoice, reopenIncomingInvoice, reverseIncomingInvoice } from '../api/incoming-invoices.api'
import { Abgleich } from '../components/Abgleich'
import { Bestellpositionen } from '../components/Bestellpositionen'
import { Freigabe, useFreigabelauf } from '../components/Freigabe'
import { KontoVorschlag, useKontovorschlag } from '../components/Kontovorschlag'
import { Lesung } from '../components/Lesung'
import { Zahlungssperre } from '../components/Zahlungssperre'
import { PositionenBlock } from '../components/PositionenBlock'
import { KontoOptionen, kontoText } from '../components/Sachkonto'
import { Statusleiste } from '../components/Statusleiste'
import { HfuPruefung, Ruecklaesse, SchlussAbzug } from '../components/Subunternehmer'
import { Zahlung } from '../components/Zahlung'
import { ART_TEXT, FELD_TEXT, MELDUNG_TEXT, MERKMAL_TEXT, STATUS_TEXT, kostenzielText } from '../lib/texte'
import type { IncomingFrame, IncomingInvoiceDetail } from '../types/incoming-invoices.types'

export default function IncomingInvoiceDetailPage() {
  const { id = '' } = useParams()
  const [rechnung, setRechnung] = useState<IncomingInvoiceDetail | null>(null)
  const [rahmen, setRahmen] = useState<IncomingFrame | null>(null)
  const [fehler, setFehler] = useState<string | null>(null)

  const lade = useCallback(async () => {
    try {
      setFehler(null)
      const [r, f] = await Promise.all([fetchIncomingInvoice(id), fetchIncomingFrame()])
      setRechnung(r)
      setRahmen(f)
    } catch (e) {
      setFehler(apiErrorText(e, 'Die Rechnung ließ sich nicht laden.'))
    }
  }, [id])
  useEffect(() => {
    setRechnung(null)
    void lade()
  }, [lade])

  if (fehler && !rechnung) {
    return (
      <div className={pageWide}>
        <Link to="/incoming-invoices" className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground">
          <ArrowLeft size={14} /> Eingangsrechnungen
        </Link>
        <div className="rounded-md bg-destructive/10 px-4 py-3 text-sm text-destructive">{fehler}</div>
      </div>
    )
  }
  if (!rechnung || !rahmen) return <div className={pageWide}><p className="text-sm text-muted-foreground">Laden…</p></div>
  return <Maske rechnung={rechnung} rahmen={rahmen} onRechnung={setRechnung} />
}

function Maske({ rechnung, rahmen, onRechnung }: { rechnung: IncomingInvoiceDetail; rahmen: IncomingFrame; onRechnung: (r: IncomingInvoiceDetail) => void }) {
  const navigate = useNavigate()
  const terms = useTerminology()
  const { user } = useAuthStore()
  const [fehler, setFehler] = useState<string | null>(null)
  // Was das Original zeigt: die Fundstelle einer Zeile des KI-Vorschlags, sonst ihre Seite (13.10.2026).
  const [originalZiel, setOriginalZiel] = useState<OriginalZiel | null>(null)
  const [fundstellen, setFundstellen] = useState<Fundstelle[]>([])
  const zeigeImOriginal = useCallback((id: string, seite: number | null) => setOriginalZiel((z) => ({ id, seite, n: (z?.n ?? 0) + 1 })), [])
  /** Klick auf eine Markierung: Die Zeile des Vorschlags dazu rückt ins Bild. */
  const zeigeZeile = (id: string) => {
    setOriginalZiel((z) => ({ id, seite: null, n: z?.n ?? 0 }))
    Array.from(document.querySelectorAll('[data-fundstelle]'))
      .find((el) => el.getAttribute('data-fundstelle') === id)
      ?.scrollIntoView({ behavior: 'smooth', block: 'center' })
  }
  const ladeOriginal = useCallback(() => fetchIncomingOriginal(rechnung.id), [rechnung.id])
  // Nachtrag 3, E-7: Der Abgleich nimmt die volle Breite; das Original klappt zur Seite, bis jemand es zurückholt.
  const [abgleichOffen, setAbgleichOffen] = useState(false)
  const [originalImAbgleich, setOriginalImAbgleich] = useState(false)
  const oeffneAbgleich = () => {
    setOriginalImAbgleich(false)
    setAbgleichOffen(true)
    window.scrollTo({ top: 0 })
  }
  const ohneOriginal = abgleichOffen && !originalImAbgleich
  const [lauf, setLauf] = useFreigabelauf(rechnung)
  const teilung = useTeilung({ schluessel: 'split_incoming_invoice', label: 'Breite des Originals' })
  const gesperrt = !rechnung.editable
  const darfLoeschen = erfuelltRecht('incoming_invoices.invoice.delete', user?.permissions) && (rechnung.status === 'UNCAPTURED' || rechnung.status === 'DRAFT')

  const stelle = useSpeicherstelle<IncomingInvoiceDetail>({
    schluessel: `incoming-invoice:${rechnung.id}`,
    bezeichnung: `Eingangsrechnung ${rechnung.number ?? ''}`.trim(),
    daten: rechnung,
    schreibe: async (patch, stand) => {
      const r = await patchIncomingInvoice(rechnung.id, patch, stand)
      onRechnung(r)
      return r
    },
    werte: (a) => a as unknown as Datensatz,
    keepalive: { url: `/incoming-invoices/${rechnung.id}`, method: 'PATCH' },
  }) as Speicherstelle<unknown>

  const f = { gesperrt }
  const art = useFeld(stelle, 'kind', { ...f, art: 'auswahl', label: 'Art' })
  const nummer = useFeld(stelle, 'supplierInvoiceNumber', { ...f, label: 'Rechnungsnummer' })
  const rechnungsdatum = useFeld(stelle, 'invoiceDate', { ...f, art: 'datum', label: 'Rechnungsdatum' })
  const eingang = useFeld(stelle, 'receivedDate', { ...f, art: 'datum', label: 'Eingangsdatum' })
  const zahlungsziel = useFeld<number | null>(stelle, 'paymentTermsDays', { ...f, art: 'zahl', stellen: 0, label: 'Zahlungsziel' })
  const faellig = useFeld(stelle, 'dueDate', { ...f, art: 'datum', label: 'Fällig am' })
  const skonto = useFeld<number | null>(stelle, 'discountPercent', { ...f, art: 'zahl', stellen: 2, label: 'Skonto' })
  const skontoBis = useFeld(stelle, 'discountDueDate', { ...f, art: 'datum', label: 'Skonto bis' })
  const iban = useFeld(stelle, 'paymentIban', { ...f, label: 'IBAN laut Rechnung' })
  const referenz = useFeld(stelle, 'paymentReference', { ...f, label: 'Zahlungsreferenz' })
  const netto = useFeld<number | null>(stelle, 'statedNet', { ...f, art: 'zahl', schreib: geldEingabe, label: 'Netto laut Rechnung' })
  const steuer = useFeld<number | null>(stelle, 'statedTax', { ...f, art: 'zahl', schreib: geldEingabe, label: 'Steuer laut Rechnung' })
  const brutto = useFeld<number | null>(stelle, 'statedGross', { ...f, art: 'zahl', schreib: geldEingabe, label: 'Brutto laut Rechnung' })
  const merkmale = useFeld<boolean>(stelle, 'formalChecked', { ...f, art: 'schalter', label: 'Rechnungsmerkmale geprüft' })
  const notiz = useFeld(stelle, 'note', { ...f, label: 'Notiz' })
  const ziel = useFeld<IncomingCostTarget | null>(stelle, 'defaultCostTarget', { ...f, art: 'auswahl', label: 'Standard-Kostenziel' })
  const zielKategorie = useFeld(stelle, 'defaultOverheadCategoryId', { ...f, art: 'auswahl', label: 'Kategorie' })
  const zielProjekt = useFeld(stelle, 'defaultProjectId', { ...f, art: 'auswahl', label: terms.project })
  // Nachtrag 6, PT-23: Das Kostenziel des Kopfs belegt neue Positionen vor — wer es erst setzt, wenn sie schon dastehen,
  // trägt es mit einem Klick in sie ein. Gezählt wird mit der Regel des Servers (`kostenzielZumKopf`).
  const zielLagen = rechnung.lines.map((z) => kostenzielZumKopf(z, rechnung))
  const zielFehlt = zielLagen.filter((l) => l === 'MISSING').length
  const zielAnders = zielLagen.filter((l) => l === 'OTHER').length
  const zielInPositionen = async (welche: 'MISSING' | 'ALL') => {
    if (welche === 'ALL' && !(await bestaetige(`Das Kostenziel des Kopfs für alle Positionen übernehmen? ${zielAnders === 1 ? 'Die Position mit einem anderen Kostenziel verliert ihres' : `Die ${zielAnders} Positionen mit einem anderen Kostenziel verlieren ihres`}.`, { ok: 'Übernehmen' }))) return
    try {
      setFehler(null)
      onRechnung(await patchIncomingInvoice(rechnung.id, { costTargetForLines: welche }, rechnung.updatedAt))
    } catch (e) {
      setFehler(apiErrorText(e, 'Das Kostenziel ließ sich nicht in die Positionen eintragen.'))
    }
  }
  // Sachkonto der ganzen Rechnung (Auftraggeber 6.10.2026): mit Stamm eine Auswahl, ohne ihn (Modus „kontiert") freier Text
  const mitStamm = rahmen.ledgerAccounts.length > 0
  const mitKonto = mitStamm || rahmen.bookkeepingMode === 'POSTED'
  const konto = useFeld(stelle, 'defaultExpenseAccount', { ...f, art: mitStamm ? 'auswahl' : 'text', label: 'Sachkonto' })
  const kontoAbweichend = rechnung.defaultExpenseAccount ? rechnung.lines.filter((z) => (z.lineType === 'ITEM' || z.lineType === 'CHARGE') && z.expenseAccount !== rechnung.defaultExpenseAccount).length : 0
  const kiKonto = useKontovorschlag(rechnung, onRechnung, setFehler)
  const lieferant = useFeld(stelle, 'supplierId', { ...f, art: 'auswahl', label: 'Lieferant' })
  const haftung = useFeld<boolean>(stelle, 'aghApplies', { ...f, art: 'schalter', label: 'Auftraggeberhaftung' })
  const vertragsart = useFeld(stelle, 'aghContractType', { ...f, art: 'auswahl', label: 'Vertragsart' })
  const neuLaden = () => void fetchIncomingInvoice(rechnung.id).then(onRechnung).catch(() => undefined)
  /** Handlung: Auch die von Hand gesetzten Positionen bekommen das Sachkonto der Rechnung. */
  const kontoFuerAlle = async () => {
    if (!(await bestaetige(`Sachkonto ${kontoText(rahmen.ledgerAccounts, rechnung.defaultExpenseAccount)} für alle Positionen übernehmen? ${kontoAbweichend === 1 ? 'Die abweichende Position verliert' : `Die ${kontoAbweichend} abweichenden Positionen verlieren`} ihr eigenes Sachkonto.`, { ok: 'Übernehmen' }))) return
    try {
      setFehler(null)
      onRechnung(await patchIncomingInvoice(rechnung.id, { expenseAccountForAllLines: true }, rechnung.updatedAt))
    } catch (e) {
      setFehler(apiErrorText(e, 'Das Sachkonto ließ sich nicht übernehmen.'))
    }
  }

  const loeschen = async () => {
    if (!(await bestaetige('Rechnung löschen? Die Rechnung und ihr Original werden gelöscht. Das lässt sich nicht rückgängig machen.', { ok: 'Löschen', gefaehrlich: true }))) return
    try {
      await deleteIncomingInvoice(rechnung.id)
      navigate(rechnung.status === 'UNCAPTURED' ? '/incoming-invoices/inbox' : '/incoming-invoices')
    } catch (e) {
      setFehler(apiErrorText(e, 'Die Rechnung ließ sich nicht löschen.'))
    }
  }

  /** 11.6 — der eine Übergang: prüfen, dann freigeben oder den Freigabelauf starten. */
  const [schliesstAb, setSchliesstAb] = useState(false)
  const abschliessen = async () => {
    try {
      setSchliesstAb(true)
      setFehler(null)
      onRechnung(await completeIncomingInvoice(rechnung.id))
      void freigabenZahlNeu()
    } catch (e) {
      setFehler(apiErrorText(e, 'Die Rechnung ließ sich nicht abschließen.'))
      // Was sperrt, steht in der Prüfung — sie zeigt den Stand des Servers.
      fetchIncomingInvoice(rechnung.id).then(onRechnung).catch(() => undefined)
    } finally {
      setSchliesstAb(false)
    }
  }
  const sperrt = rechnung.checks.blocking.length > 0

  // 5.2 — die drei Korrekturwege
  const darfStornieren = erfuelltRecht('incoming_invoices.invoice.cancel', user?.permissions)
  const darfOeffnen = erfuelltRecht('incoming_invoices.invoice.release', user?.permissions)
  const korrigiere = async (was: () => Promise<IncomingInvoiceDetail>, fehlertext: string, danach?: (r: IncomingInvoiceDetail) => void) => {
    try {
      setFehler(null)
      const r = await was()
      if (danach) danach(r)
      else onRechnung(r)
      void freigabenZahlNeu()
    } catch (e) {
      setFehler(apiErrorText(e, fehlertext))
    }
  }
  const stornieren = async () => {
    const grund = await frage(`Eingangsrechnung ${rechnung.number ?? ''} stornieren? Sie bleibt mit dem Grund sichtbar, zählt aber nirgends mehr mit. Grund:`, '', { pflicht: true, ok: 'Stornieren' })
    if (grund) await korrigiere(() => cancelIncomingInvoice(rechnung.id, grund), 'Die Rechnung ließ sich nicht stornieren.')
  }
  const oeffnen = async () => {
    if (!(await bestaetige('Zur Bearbeitung öffnen? Die Rechnung wird wieder ein Entwurf; Prüfung und Freigabe gelten nicht mehr und laufen beim Abschließen neu.', { ok: 'Öffnen' }))) return
    await korrigiere(() => reopenIncomingInvoice(rechnung.id), 'Die Rechnung ließ sich nicht öffnen.')
  }
  const stornobuchung = async () => {
    if (!(await bestaetige(`Stornobuchung zu ${rechnung.number ?? ''} anlegen? Es entsteht eine Rechnung mit negativen Beträgen, die im nächsten Stapel an die Buchhaltung geht. Die richtige Rechnung erfasst du danach neu.`, { ok: 'Stornobuchung anlegen' }))) return
    await korrigiere(() => reverseIncomingInvoice(rechnung.id), 'Die Stornobuchung ließ sich nicht anlegen.', (r) => navigate(`/incoming-invoices/${r.id}`))
  }

  const status = STATUS_TEXT[rechnung.status]
  const abweichung = (laut: number | null, gerechnet: number) => laut !== null && Math.abs(Math.round((laut - gerechnet) * 100)) > Math.max(1, rechnung.taxes.length)

  return (
    <div className={pageWide}>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="min-w-0">
          <Link to={rechnung.status === 'UNCAPTURED' ? '/incoming-invoices/inbox' : '/incoming-invoices'} className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground">
            <ArrowLeft size={14} /> {rechnung.status === 'UNCAPTURED' ? 'Eingangsstapel' : 'Eingangsrechnungen'}
          </Link>
          <h1 className="mt-1 flex flex-wrap items-center gap-3 text-2xl font-bold text-foreground">
            <span>{rechnung.number ? `Eingangsrechnung ${rechnung.number}` : 'Neue Eingangsrechnung'}</span>
            <span className={`inline-flex items-center rounded-full px-3 py-1 text-sm font-semibold ${status.klasse}`}>{status.label}</span>
          </h1>
          {/* Lieferant und Art sind das, woran man die Rechnung erkennt — fett (Auftraggeber 5.10.2026) */}
          {rechnung.supplierName ? (
            <p className="mt-0.5 flex flex-wrap items-baseline gap-x-3 text-lg text-foreground">
              <strong>{rechnung.supplierName}</strong>
              <strong className="text-base">{ART_TEXT[rechnung.kind]}</strong>
              {rechnung.supplierInvoiceNumber && <span className="text-sm text-muted-foreground">Nr. {rechnung.supplierInvoiceNumber}</span>}
            </p>
          ) : (
            <p className="text-sm text-muted-foreground">Noch kein Lieferant — wähle ihn, damit die Rechnung eine Eingangsnummer bekommt.</p>
          )}
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {darfLoeschen && (
            <button type="button" className="inline-flex items-center gap-1 rounded-md border border-border px-3 py-1.5 text-sm text-destructive hover:bg-destructive/10" onClick={() => void loeschen()}>
              <Trash2 size={14} /> Löschen
            </button>
          )}
          {darfStornieren && ['DRAFT', 'REJECTED', 'IN_APPROVAL', 'RELEASED'].includes(rechnung.status) && (
            <button type="button" className="rounded-md border border-border px-3 py-1.5 text-sm text-destructive hover:bg-destructive/10" onClick={() => void stornieren()}>
              Stornieren
            </button>
          )}
          {darfOeffnen && rechnung.status === 'RELEASED' && rechnung.paidAmount === 0 && (
            <button type="button" className="rounded-md border border-border px-3 py-1.5 text-sm hover:bg-muted" onClick={() => void oeffnen()}>
              Zur Bearbeitung öffnen
            </button>
          )}
          {darfStornieren && rechnung.status === 'EXPORTED' && !rechnung.cancelsInvoiceId && (
            <button type="button" className="rounded-md border border-border px-3 py-1.5 text-sm hover:bg-muted" title="Eine übergebene Rechnung bleibt, wie sie ist — korrigiert wird mit einer Gegenbuchung" onClick={() => void stornobuchung()}>
              Stornobuchung
            </button>
          )}
          {rechnung.completable && (
            <button
              type="button"
              className="inline-flex items-center gap-1 rounded-md bg-primary px-4 py-1.5 text-sm font-medium text-primary-foreground hover:opacity-90 disabled:opacity-50"
              disabled={schliesstAb || sperrt}
              // Der Name des Knopfs bleibt „Abschließen" — ein `title` allein würde ihn für Vorlesehilfen ersetzen.
              aria-label="Abschließen"
              title={sperrt ? 'Vor dem Abschließen ist noch etwas zu beheben — siehe Prüfung' : 'Prüft die Rechnung und gibt sie frei oder startet den Freigabelauf'}
              onClick={() => void abschliessen()}
            >
              <Send size={14} /> Abschließen
            </button>
          )}
        </div>
      </div>

      {fehler && <div className="rounded-md bg-destructive/10 px-4 py-3 text-sm text-destructive">{fehler}</div>}
      {/* Nachtrag 1, 6.1: Der Posteingang hat den Absender erkannt — ein Vorschlag, bestätigt wird hier. */}
      {rechnung.suggestedSupplier && rechnung.editable && (
        <div className="flex flex-wrap items-center gap-3 rounded-md bg-sky-500/10 px-4 py-2 text-sm">
          <span>
            Aus dem Posteingang — als Lieferant erkannt: <strong>{rechnung.suggestedSupplier.name}</strong>
          </span>
          <button
            type="button"
            className="rounded-md border border-border bg-background px-3 py-1 hover:bg-muted"
            onClick={() => {
              const id = rechnung.suggestedSupplier?.id
              if (!id) return
              setFehler(null)
              patchIncomingInvoice(rechnung.id, { supplierId: id }, rechnung.updatedAt)
                .then(onRechnung)
                .catch((e) => setFehler(apiErrorText(e, 'Der Lieferant ließ sich nicht übernehmen.')))
            }}
          >
            Übernehmen
          </button>
        </div>
      )}
      {rechnung.status === 'CANCELLED' && <div className="rounded-md bg-muted px-4 py-3 text-sm">Storniert{rechnung.cancelReason ? `: ${rechnung.cancelReason}` : '.'}</div>}
      {rechnung.cancelsInvoiceId && (
        <div className="rounded-md bg-muted px-4 py-3 text-sm">
          Stornobuchung —{' '}
          <Link className="underline hover:text-primary" to={`/incoming-invoices/${rechnung.cancelsInvoiceId}`}>
            zur ursprünglichen Rechnung
          </Link>
        </div>
      )}

      {/* Nebeneinander ab `xl`; der Rand dazwischen lässt sich ziehen (Auftraggeber 5.10.2026). Darunter stehen beide untereinander. */}
      <div
        ref={teilung.behaelter}
        className={`grid gap-4 xl:gap-0 ${ohneOriginal ? '' : 'xl:grid-cols-[minmax(0,var(--er-links))_1rem_minmax(0,var(--er-rechts))]'} ${teilung.zieht ? 'select-none' : ''}`}
        style={{ '--er-links': `${teilung.anteil}fr`, '--er-rechts': `${100 - teilung.anteil}fr` } as CSSProperties}
      >
        <div className={ohneOriginal ? 'hidden' : 'xl:sticky xl:top-4 xl:h-[calc(100vh-7rem)]'}>
          <OriginalAnzeige
            schluessel={rechnung.id}
            lade={ladeOriginal}
            vorhanden={rechnung.hasFile}
            ziel={originalZiel}
            fundstellen={fundstellen}
            onFundstelle={zeigeZeile}
            leer="Zu dieser Rechnung ist kein Original hinterlegt — sie wurde von Hand angelegt."
          />
        </div>
        {/* Der Griff steht so hoch wie das Original und bleibt mit ihm stehen — er ist dessen rechter Rand. */}
        <div {...teilung.griff} className={`group hidden cursor-col-resize touch-none focus:outline-none ${ohneOriginal ? '' : 'xl:sticky xl:top-4 xl:flex xl:h-[calc(100vh-7rem)] xl:justify-center'}`}>
          <span className={`h-full w-1 rounded-full transition-colors group-hover:bg-primary/60 group-focus-visible:bg-primary ${teilung.zieht ? 'bg-primary' : 'bg-transparent'}`} />
        </div>

        {abgleichOffen && (
          <div className="min-w-0 space-y-4">
            <Statusleiste rechnung={rechnung} lauf={lauf} />
            <Abgleich rechnung={rechnung} onRechnung={onRechnung} onFehler={setFehler} onSchliessen={() => setAbgleichOffen(false)} originalSichtbar={originalImAbgleich} onOriginal={setOriginalImAbgleich} />
          </div>
        )}
        {/* Die Erfassung bleibt eingehängt, während der Abgleich offen ist — ihre Felder behalten Stand und Warteschlange. */}
        <div className={abgleichOffen ? 'hidden' : 'min-w-0 space-y-4'}>
          <Statusleiste rechnung={rechnung} lauf={lauf} />
          {/* Nach der Freigabe steht die Sperre im Abschnitt „Zahlung" — davor gibt es ihn nicht (Nachtrag 3, E-4). */}
          {rechnung.status !== 'RELEASED' && rechnung.status !== 'EXPORTED' && rechnung.status !== 'CANCELLED' && <Zahlungssperre rechnung={rechnung} onRechnung={onRechnung} onFehler={setFehler} />}

          {rechnung.status !== 'UNCAPTURED' && rechnung.status !== 'DRAFT' && (
            // Solange die Rechnung auf jemanden wartet, trägt der Abschnitt die Farbe der Statusleiste.
            <Abschnitt titel="Freigabe" className={rechnung.status === 'IN_APPROVAL' ? '!border-2 !border-amber-500/60' : rechnung.status === 'REJECTED' ? '!border-2 !border-destructive/60' : ''}>
              <Freigabe rechnung={rechnung} lauf={lauf} setLauf={setLauf} onRechnung={onRechnung} onFehler={setFehler} />
            </Abschnitt>
          )}

          {(rechnung.status === 'RELEASED' || rechnung.status === 'EXPORTED') && (
            <Abschnitt titel="Zahlung">
              {rahmen.aghEnabled && rechnung.aghApplies && rechnung.supplierId && (
                <div className="mb-3">
                  <HfuPruefung rechnung={rechnung} onFehler={setFehler} onGeaendert={neuLaden} />
                </div>
              )}
              <Zahlung rechnung={rechnung} onRechnung={onRechnung} onFehler={setFehler} />
            </Abschnitt>
          )}

          {rechnung.status !== 'UNCAPTURED' && (rechnung.kind === 'PARTIAL_INVOICE' || rechnung.kind === 'FINAL_INVOICE' || rechnung.retentionTotal > 0) && (
            <Abschnitt titel="Rücklässe">
              <Ruecklaesse rechnung={rechnung} onFehler={setFehler} onGeaendert={neuLaden} />
            </Abschnitt>
          )}

          {/* Nachtrag 2, 5.3: der Vorschlag der KI — eine eigene Schicht über der Maske, nur mit gebuchtem Baustein */}
          {rahmen.aiCapture && rechnung.hasFile && <Lesung rechnung={rechnung} onRechnung={onRechnung} onZeige={zeigeImOriginal} onFundstellen={setFundstellen} gewaehlt={originalZiel?.id ?? null} onFehler={setFehler} onAbgleich={rahmen.matching && rechnung.supplierId ? oeffneAbgleich : undefined} />}

          {rahmen.aiCapture && rechnung.hasFile && (
            <div className="flex items-center gap-3 pt-1 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              <span className="h-px flex-1 bg-border" />
              Die Rechnung — was hier steht, gilt
              <span className="h-px flex-1 bg-border" />
            </div>
          )}

          <Abschnitt titel="Kopf">
            <div className="grid gap-x-8 gap-y-5 lg:grid-cols-2">
              <Gruppe titel="Rechnung">
                <FeldZeile label="Lieferant" status={lieferant}>
                  {rechnung.supplierId && gesperrt ? (
                    <span className="block py-1.5 font-semibold">{rechnung.supplierName}</span>
                  ) : (
                    <LieferantFeld fett anzeige={rechnung.supplierName ?? ''} gewaehlt={rechnung.supplierId} gesperrt={gesperrt} onWahl={(id) => lieferant.setze(id)} />
                  )}
                </FeldZeile>
                <FeldZeile label="Art" status={art}>
                  <select className={`${feldKlasse} font-semibold`} {...art.props}>
                    {INCOMING_INVOICE_KINDS.map((k) => (
                      <option key={k} value={k}>
                        {ART_TEXT[k]}
                      </option>
                    ))}
                  </select>
                </FeldZeile>
                <FeldZeile label="Rechnungsnummer" status={nummer}>
                  <input className={feldKlasse} {...nummer.props} />
                </FeldZeile>
                <FeldZeile label="Rechnungsdatum" status={rechnungsdatum}>
                  {/* fett wie Lieferant und Art: das Datum, nach dem man die Rechnung sucht (Auftraggeber 6.10.2026) */}
                  <input type="date" className={`${feldKlasse} font-semibold ${datumOhneVorlage(rechnungsdatum.props.value)}`} {...rechnungsdatum.props} />
                </FeldZeile>
                <Leistungszeitraum stelle={stelle} gesperrt={gesperrt} />
                <FeldZeile label="Eingangsdatum" status={eingang}>
                  <input type="date" className={`${feldKlasse} ${datumOhneVorlage(eingang.props.value)}`} {...eingang.props} />
                </FeldZeile>
              </Gruppe>
              <Gruppe titel="Zahlung">
                <FeldZeile label="Zahlungsziel" status={zahlungsziel} hilfe="Tage — die Fälligkeit folgt, solange du sie nicht selbst setzt">
                  <input inputMode="numeric" className={feldKlasse} {...zahlungsziel.props} />
                </FeldZeile>
                <FeldZeile label="Fällig am" status={faellig}>
                  <input type="date" className={`${feldKlasse} ${datumOhneVorlage(faellig.props.value)}`} {...faellig.props} />
                </FeldZeile>
                <FeldZeile label="Skonto %" status={skonto}>
                  <input inputMode="decimal" className={feldKlasse} {...skonto.props} />
                </FeldZeile>
                <FeldZeile label="Skonto bis" status={skontoBis}>
                  <input type="date" className={`${feldKlasse} ${datumOhneVorlage(skontoBis.props.value)}`} {...skontoBis.props} />
                </FeldZeile>
                <FeldZeile label="IBAN laut Rechnung" status={iban}>
                  <input className={feldKlasse} {...iban.props} />
                </FeldZeile>
                <FeldZeile label="Zahlungsreferenz" status={referenz}>
                  <input className={feldKlasse} {...referenz.props} />
                </FeldZeile>
              </Gruppe>
              <Gruppe titel="Vorgaben für die Positionen">
                <FeldZeile
                  label="Standard-Kostenziel"
                  status={ziel}
                  hilfe={
                    gesperrt || (!zielFehlt && !zielAnders) ? (
                      'Vorbelegung neuer Positionen'
                    ) : (
                      <>
                        Vorbelegung neuer Positionen
                        {zielFehlt > 0 && (
                          <span className="block">
                            {zielFehlt === 1 ? '1 Position ohne Kostenziel' : `${zielFehlt} Positionen ohne Kostenziel`}
                            {' · '}
                            <button type="button" className="underline hover:text-foreground" onClick={() => void zielInPositionen('MISSING')}>
                              {zielFehlt === 1 ? 'Dort eintragen' : 'In diese eintragen'}
                            </button>
                          </span>
                        )}
                        {zielAnders > 0 && (
                          <span className="block">
                            {zielAnders === 1 ? '1 Position mit anderem Kostenziel' : `${zielAnders} Positionen mit anderem Kostenziel`}
                            {' · '}
                            <button type="button" className="underline hover:text-foreground" onClick={() => void zielInPositionen('ALL')}>
                              Für alle Positionen übernehmen
                            </button>
                          </span>
                        )}
                      </>
                    )
                  }
                >
                  {/* Projekt bzw. Kategorie rückt unter das Kostenziel, wo die Spalte für beide nebeneinander zu schmal ist */}
                  <span className="flex flex-wrap gap-2">
                    <select className={`${feldKlasse} !w-40 shrink-0`} {...ziel.props}>
                      <option value="">—</option>
                      {(['PROJECT', 'STOCK', 'OVERHEAD'] as const).map((z) => (
                        <option key={z} value={z}>
                          {kostenzielText(z, terms.project)}
                        </option>
                      ))}
                    </select>
                    {ziel.wert === 'OVERHEAD' && (
                      <select aria-label="Gemeinkosten-Kategorie" className={`${feldKlasse} min-w-[10rem] flex-1`} {...zielKategorie.props}>
                        <option value="">Kategorie wählen …</option>
                        {rahmen.overheadCategories.map((k) => (
                          <option key={k.id} value={k.id}>
                            {k.name}
                          </option>
                        ))}
                      </select>
                    )}
                    {ziel.wert === 'PROJECT' && <ProjektSuche gesperrt={gesperrt} gewaehlt={zielProjekt.wert} label={terms.project} onWahl={(pid) => zielProjekt.setze(pid)} />}
                  </span>
                </FeldZeile>
                {/* Was nicht eingerichtet ist, wird nicht angeboten: erst mit dem ersten Sachkonto des Stamms */}
                {mitKonto && (
                  <FeldZeile
                    label="Sachkonto"
                    status={konto}
                    hilfe={
                      <>
                        {/* AP-44 7.6: Bekämen alle Positionen dasselbe Konto, schlägt die KI es hier vor — es steht neben dem Feld, nicht darin */}
                        {rechnung.accountSuggestion && !gesperrt && (
                          <span className="mb-0.5 block text-foreground">
                            <KontoVorschlag
                              vorschlag={rechnung.accountSuggestion}
                              konten={rahmen.ledgerAccounts}
                              laeuft={kiKonto.laeuft}
                              onUebernehmen={() => void kiKonto.uebernehmen({ invoice: true })}
                              onVerwerfen={() => void kiKonto.verwerfen({ invoice: true })}
                            />
                          </span>
                        )}
                        {kontoAbweichend > 0 ? (
                          <>
                            {kontoAbweichend === 1 ? '1 Position weicht ab' : `${kontoAbweichend} Positionen weichen ab`}
                            {!gesperrt && (
                              <>
                                {' · '}
                                <button type="button" className="underline hover:text-foreground" onClick={() => void kontoFuerAlle()}>
                                  Für alle Positionen übernehmen
                                </button>
                              </>
                            )}
                          </>
                        ) : (
                          'gilt für jede Position — einzelne lassen sich an der Position abweichend setzen'
                        )}
                      </>
                    }
                  >
                    {mitStamm ? (
                      <select className={feldKlasse} {...konto.props}>
                        <option value="">— je Position</option>
                        <KontoOptionen konten={rahmen.ledgerAccounts} aktuell={rechnung.defaultExpenseAccount} />
                      </select>
                    ) : (
                      <input className={feldKlasse} {...konto.props} />
                    )}
                  </FeldZeile>
                )}
              </Gruppe>
              <Gruppe titel="Sonstiges">
                <FeldZeile label="Notiz" status={notiz}>
                  <input className={feldKlasse} {...notiz.props} />
                </FeldZeile>
                {rahmen.aghEnabled && (
                  <>
                    <SchalterZeile feld={haftung} label="Auftraggeberhaftung" hinweis="Bauleistung eines Subunternehmers — bei einer reinen Materiallieferung abschalten" />
                    {rechnung.aghApplies && (
                      <FeldZeile label="Vertragsart" status={vertragsart} hilfe="bestimmt den Satz des Haftungsbetrags">
                        <select className={feldKlasse} {...vertragsart.props}>
                          <option value="WORK">Werkvertrag</option>
                          <option value="LABOUR_LEASING">Arbeitskräfteüberlassung</option>
                        </select>
                      </FeldZeile>
                    )}
                  </>
                )}
              </Gruppe>
            </div>
          </Abschnitt>

          <div className="grid gap-4 lg:grid-cols-2">
            <Abschnitt titel="Summen">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-xs text-muted-foreground">
                    <th className="pb-1 font-normal" />
                    <th className="pb-1 text-right font-normal">laut Rechnung</th>
                    <th className="pb-1 text-right font-normal">gerechnet</th>
                  </tr>
                </thead>
                <tbody>
                  {(
                    [
                      ['Netto', netto, rechnung.statedNet, rechnung.netTotal],
                      ['Steuer', steuer, rechnung.statedTax, rechnung.taxTotal],
                      ['Brutto', brutto, rechnung.statedGross, rechnung.grossTotal],
                    ] as const
                  ).map(([label, feld, laut, gerechnet]) => (
                    <tr key={label}>
                      <td className="py-0.5 text-muted-foreground">{label}</td>
                      <td className="py-0.5">
                        {/* Preisformat wie jeder Betrag: ohne Fokus 1.234,50, beim Bearbeiten ohne Tausenderpunkt */}
                        <GeldFeldFuer aria-label={`${label} laut Rechnung`} className={feldKlasse} feld={feld.props} />
                      </td>
                      <td className={`py-0.5 pl-3 text-right tabular-nums ${abweichung(laut, gerechnet) ? 'font-semibold text-destructive' : ''}`}>{geldText(gerechnet)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {rechnung.taxes.length > 0 && (
                <ul className="mt-2 space-y-0.5 border-t border-border pt-2 text-xs text-muted-foreground">
                  {rechnung.taxes.map((t) => (
                    <li key={`${t.taxCodeKey}-${t.taxRate}`} className="flex justify-between tabular-nums">
                      <span>
                        {t.taxCodeKey} · {t.taxRate} % auf {geldText(t.netAmount)}
                      </span>
                      <span>{geldText(t.taxAmount)}</span>
                    </li>
                  ))}
                </ul>
              )}
              <p className="mt-2 text-xs text-muted-foreground">Währung {rechnung.currency}. Übergeben wird laut Rechnung; eine Abweichung über einem Cent je Steuersatz braucht eine Freigabe.</p>
            </Abschnitt>

            <Abschnitt titel="Prüfung">
              <SchalterZeile feld={merkmale} label="Rechnungsmerkmale geprüft" hinweis="Name und Anschrift beider Seiten, UID, Leistungsdatum, fortlaufende Nummer (§ 11 UStG)" />
              <Pruefliste rechnung={rechnung} />
            </Abschnitt>
          </div>

          <Abschnitt titel="Positionen">
            {/* Was nicht gebucht ist, wird nicht angeboten: ohne Belegwesen kein Abgleich */}
            {rahmen.matching && rechnung.supplierId && (
              <div className="mb-3">
                <Bestellpositionen rechnung={rechnung} onRechnung={onRechnung} onFehler={setFehler} onAbgleich={oeffneAbgleich} />
              </div>
            )}
            {rechnung.kind === 'FINAL_INVOICE' && rechnung.editable && <SchlussAbzug rechnung={rechnung} onFehler={setFehler} onRechnung={onRechnung} />}
            <PositionenBlock rechnung={rechnung} rahmen={rahmen} onRechnung={onRechnung} onFehler={setFehler} />
          </Abschnitt>
        </div>
      </div>
    </div>
  )
}

/**
 * Eine Gruppe zusammengehöriger Felder im Kopf (Auftraggeber 6.10.2026): Überschrift mit Linie, darunter die
 * Felder untereinander. Vorher lief ein Raster mit sechzehn Feldern in zwei Spalten durch — was zusammengehört,
 * stand nebeneinander oder auch nicht. Jetzt liest sich jede Gruppe von oben nach unten: was die Rechnung ist,
 * wie sie zu zahlen ist, was für ihre Positionen gilt.
 */
function Gruppe({ titel, children }: { titel: string; children: ReactNode }) {
  return (
    <div role="group" aria-label={titel} className="min-w-0">
      <h4 className="mb-2 border-b border-border pb-1 text-xs font-semibold uppercase tracking-wide text-muted-foreground">{titel}</h4>
      <div className="space-y-2">{children}</div>
    </div>
  )
}

/**
 * Von/Bis des Leistungszeitraums — eine Gruppe: Bis nicht vor Von. Beide Felder stehen zusammen in **einer**
 * Spalte und sind miteinander so breit wie das Feld darüber (Auftraggeber 6.10.2026); über beide Spalten
 * gezogen standen die zwei Daten weit auseinander.
 */
function Leistungszeitraum({ stelle, gesperrt }: { stelle: Speicherstelle<unknown>; gesperrt: boolean }) {
  return (
    <Feldgruppe stelle={stelle} pruefe={(w) => (w.serviceDateFrom && w.serviceDateTo && String(w.serviceDateTo) < String(w.serviceDateFrom) ? '„bis" liegt vor „von"' : null)} label="Leistungszeitraum">
      <LeistungFelder stelle={stelle} gesperrt={gesperrt} />
    </Feldgruppe>
  )
}
function LeistungFelder({ stelle, gesperrt }: { stelle: Speicherstelle<unknown>; gesperrt: boolean }) {
  const von = useFeld(stelle, 'serviceDateFrom', { gesperrt, art: 'datum', label: 'Leistung von' })
  const bis = useFeld(stelle, 'serviceDateTo', { gesperrt, art: 'datum', label: 'Leistung bis' })
  // Nebeneinander, solange jedes Datum Platz hat; in einer sehr schmalen Spalte rückt „bis" unter „von".
  const datum = `${feldKlasse} min-w-[7.25rem] flex-1`
  return (
    <FeldZeile label="Leistung von – bis" status={von.fehler ? von : bis}>
      <span className="flex flex-wrap gap-2">
        <input aria-label="Leistung von" type="date" className={`${datum} ${datumOhneVorlage(von.props.value)}`} {...von.props} />
        <input aria-label="Leistung bis" type="date" className={`${datum} ${datumOhneVorlage(bis.props.value)}`} {...bis.props} />
      </span>
    </FeldZeile>
  )
}

function LieferantFeld({ anzeige, gewaehlt, gesperrt, fett, onWahl }: { anzeige: string; gewaehlt: string | null; gesperrt: boolean; fett?: boolean; onWahl: (id: string) => void }) {
  const [text, setText] = useState<string | null>(null)
  const [treffer, setTreffer] = useSuche(text ?? '', text !== null, lookupSuppliers)
  useEffect(() => setText(null), [gewaehlt])
  return (
    <span className="relative block">
      <input
        aria-label="Lieferant"
        // fett nur der gewählte Lieferant, nicht der Suchtext
        className={`${feldKlasse} ${fett && text === null && anzeige ? 'font-semibold' : ''}`}
        disabled={gesperrt}
        placeholder="Name, Lieferantennummer, UID oder IBAN …"
        value={text ?? anzeige}
        onChange={(e) => setText(e.target.value)}
        onBlur={() => {
          setText(null)
          setTreffer([])
        }}
      />
      <Vorschlaege
        eintraege={treffer}
        schluessel={(l) => l.id}
        onWahl={(l) => {
          setTreffer([])
          setText(null)
          onWahl(l.id)
        }}
        darstellung={(l) => (
          <span>
            {l.name}
            <span className="ml-2 text-xs text-muted-foreground">{[l.supplierNumber, l.vatId].filter(Boolean).join(' · ')}</span>
          </span>
        )}
      />
    </span>
  )
}

function ProjektSuche({ gewaehlt, gesperrt, label, onWahl }: { gewaehlt: string | null; gesperrt: boolean; label: string; onWahl: (id: string) => void }) {
  const [text, setText] = useState('')
  const [name, setName] = useState<string | null>(null)
  const [treffer, setTreffer] = useSuche(text, !!text, lookupProjects)
  return (
    <span className="relative block min-w-[10rem] flex-1">
      <input
        aria-label={label}
        className={feldKlasse}
        disabled={gesperrt}
        placeholder={gewaehlt ? (name ?? `${label} gewählt`) : `${label} suchen …`}
        value={text}
        onChange={(e) => setText(e.target.value)}
        onBlur={() => {
          setText('')
          setTreffer([])
        }}
      />
      <Vorschlaege
        eintraege={treffer}
        schluessel={(p) => p.id}
        onWahl={(p) => {
          setTreffer([])
          setText('')
          setName(`${p.projectNumber} ${p.name}`)
          onWahl(p.id)
        }}
        darstellung={(p) => (
          <span>
            <span className="tabular-nums">{p.projectNumber}</span> {p.name}
          </span>
        )}
      />
    </span>
  )
}

/** 10. — Ergebnis der Prüfung: blockierend, Freigabeanlass, Hinweis. */
function Pruefliste({ rechnung }: { rechnung: IncomingInvoiceDetail }) {
  const { blocking, reasons, hints } = rechnung.checks
  const zeile = (z: string | undefined) => {
    const p = rechnung.lines.find((l) => l.id === z)
    return p ? ` (Position ${p.positionNo})` : ''
  }
  const text = (m: { code: string; field?: string; lineId?: string; detail?: string }) => {
    if (m.code === 'REQUIRED_FIELD' && m.field) return `Fehlt: ${FELD_TEXT[m.field] ?? m.field}`
    if (m.code === 'DUPLICATE' && m.detail) return `${MELDUNG_TEXT.DUPLICATE} (Eingangsrechnung ${m.detail})`
    if (m.code === 'DUPLICATE_FILE' && m.detail) return `${MELDUNG_TEXT.DUPLICATE_FILE} (Eingangsrechnung ${m.detail})`
    if (m.code === 'FORMAL_DEFECT' && m.detail) return `${MELDUNG_TEXT.FORMAL_DEFECT}: ${m.detail.split(',').map((d) => MERKMAL_TEXT[d] ?? d).join('; ')}`
    if (m.code === 'DISCOUNT_EXPIRES_SOON' && m.detail) return `${MELDUNG_TEXT.DISCOUNT_EXPIRES_SOON} — noch ${m.detail} ${m.detail === '1' ? 'Tag' : 'Tage'}`
    return `${MELDUNG_TEXT[m.code] ?? m.code}${zeile(m.lineId)}`
  }
  // Der Stand auf einen Blick (Auftraggeber 5.10.2026): eine Zeile mit dem Ergebnis, darunter die Gruppen in ihrer Farbe.
  const stand = blocking.length
    ? { Symbol: XCircle, klasse: 'border-destructive/50 bg-destructive/10 text-destructive', text: `${blocking.length} ${blocking.length === 1 ? 'Punkt ist' : 'Punkte sind'} vor dem Abschließen zu beheben` }
    : reasons.length
      ? { Symbol: AlertTriangle, klasse: 'border-amber-500/50 bg-amber-500/10 text-amber-800 dark:text-amber-200', text: `Abschließbar — ${reasons.length} ${reasons.length === 1 ? 'Anlass' : 'Anlässe'} für eine Freigabe` }
      : { Symbol: CheckCircle2, klasse: 'border-emerald-500/50 bg-emerald-500/10 text-emerald-800 dark:text-emerald-200', text: 'Nichts zu beanstanden' }
  const zahlen = [
    { zahl: blocking.length, farbe: 'bg-destructive/20 text-destructive', was: 'zu beheben' },
    { zahl: reasons.length, farbe: 'bg-amber-500/25 text-amber-800 dark:text-amber-200', was: 'Freigabeanlässe' },
    { zahl: hints.length, farbe: 'bg-muted text-muted-foreground', was: 'Hinweise' },
  ].filter((z) => z.zahl > 0)
  const kopf = (
    <div className={`mt-2 flex items-center gap-2 rounded-md border px-3 py-2 ${stand.klasse}`}>
      <stand.Symbol size={20} className="shrink-0" />
      <span className="text-sm font-semibold">{stand.text}</span>
      <span className="ml-auto flex shrink-0 gap-1 text-xs font-semibold tabular-nums">
        {zahlen.map((z) => (
          <span key={z.was} className={`rounded-full px-2 py-0.5 ${z.farbe}`} title={`${z.zahl} ${z.was}`}>
            {z.zahl}
          </span>
        ))}
      </span>
    </div>
  )
  if (!blocking.length && !reasons.length && !hints.length) return kopf
  const gruppe = (titel: string, meldungen: { code: string }[], Symbol: typeof XCircle, klasse: string, rand: string) =>
    meldungen.length > 0 && (
      <div className={`mt-2 border-l-4 pl-3 ${rand}`}>
        <p className={`text-xs font-semibold uppercase tracking-wide ${klasse}`}>
          {titel} ({meldungen.length})
        </p>
        <ul className="mt-1 space-y-1">
          {meldungen.map((m, i) => (
            <li key={i} className="flex items-start gap-2 text-sm">
              <Symbol size={15} className={`mt-0.5 shrink-0 ${klasse}`} />
              <span>{text(m)}</span>
            </li>
          ))}
        </ul>
      </div>
    )
  return (
    <div>
      {kopf}
      {gruppe('Vor dem Abschließen zu beheben', blocking, XCircle, 'text-destructive', 'border-destructive/60')}
      {gruppe('Braucht eine Freigabe', reasons, AlertTriangle, 'text-amber-600 dark:text-amber-400', 'border-amber-500/60')}
      {gruppe('Hinweise', hints, Info, 'text-muted-foreground', 'border-border')}
    </div>
  )
}
